var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { NotifyPropertyChanges, Component, Property, getUniqueID, isNullOrUndefined, addClass, attributes, removeClass, remove, Event, EventHandler } from '@syncfusion/ej2-base';
const INPUTFIELD = 'e-otp-input-field';
const RTL = 'e-rtl';
/**
 * Specifies the type of input for the Otp (One-Time Password) input component.
 */
export var OtpInputType;
(function (OtpInputType) {
    /**
     * Specifies the type of input to be number for the Otp input.
     */
    OtpInputType["Number"] = "number";
    /**
     * Specifies the type of input to be text for the Otp input.
     */
    OtpInputType["Text"] = "text";
    /**
     * Specifies the type of input to be password for the Otp input.
     */
    OtpInputType["Password"] = "password";
})(OtpInputType || (OtpInputType = {}));
/**
 * Specifies the style variant for the Otp (One-Time Password) input component.
 */
export var OtpInputStyle;
(function (OtpInputStyle) {
    /**
     * Specifies the style of the Otp input to be outlined.
     */
    OtpInputStyle["Outlined"] = "outlined";
    /**
     * Specifies the style of the Otp input to be underlined.
     */
    OtpInputStyle["Underlined"] = "underlined";
    /**
     * Specifies the style of the Otp input to be filled.
     */
    OtpInputStyle["Filled"] = "filled";
})(OtpInputStyle || (OtpInputStyle = {}));
/**
 * Enum for the case transformation options for OTP input text.
 *
 * @readonly
 * @enum {string}
 */
export var TextTransform;
(function (TextTransform) {
    /**
     * No case transformation. The input text remains unchanged.
     */
    TextTransform["None"] = "none";
    /**
     * Convert the input text to uppercase.
     */
    TextTransform["Uppercase"] = "uppercase";
    /**
     * Convert the input text to lowercase.
     */
    TextTransform["Lowercase"] = "lowercase";
})(TextTransform || (TextTransform = {}));
/**
 * Represents the Otp component that allows the user to enter the otp values.
 * ```html
 * <div id='OTPInput'></div>
 * ```
 * ```typescript
 * <script>
 *   var OtpinputObj = new OtpInput();
 *   OtpinputObj.appendTo('#OTPInput');
 * </script>
 * ```
 */
let OtpInput = class OtpInput extends Component {
    constructor(options, element) {
        super(options, element);
        /* Private variables */
        this.inputs = [];
        this.previousValue = '';
        this.separatorElements = [];
        this.shouldFireFocus = true;
        this.shouldFireBlur = true;
        this.isFocusInCalled = false;
        this.isFocusOutCalled = false;
        this.handleWheelEvent = (e) => {
            e.preventDefault();
        };
    }
    preRender() {
        if (!this.element.id) {
            this.element.id = getUniqueID('e-' + this.getModuleName());
        }
    }
    render() {
        this.initialize();
    }
    initialize() {
        attributes(this.element, { 'role': 'group' });
        this.renderInputs();
        this.renderSeparator(1, this.inputs.length);
        this.addPlaceHolder();
        this.updateCssClass(this.cssClass);
        this.updateVariantClass();
        this.updateAriaLabel(this.ariaLabels);
        this.setElementAttributes(this.htmlAttributes, this.element);
        if (this.enableRtl) {
            this.element.classList.add(RTL);
        }
        this.previousValue = this.value.toString();
        if (this.autoFocus) {
            this.focusIn();
        }
    }
    renderInputs() {
        this.hiddenInputEle = this.createElement('input', {
            id: 'otpInput_hidden',
            attrs: {
                name: this.element.id,
                type: 'hidden',
                value: this.type === 'number' ? this.value.toString().replace(/\D/g, '') : this.value.toString(),
                autoComplete: 'off'
            }
        });
        this.element.appendChild(this.hiddenInputEle);
        for (let i = 0; i < this.length; i++) {
            this.createOtpInput(i);
        }
    }
    createOtpInput(index) {
        const valueContainer = this.getDefaultValue();
        let inputValue = '';
        if (valueContainer) {
            const valueAtIndex = valueContainer[parseInt(index.toString(), 10)];
            if (this.type === 'number') {
                if (!isNaN(Number(valueAtIndex))) {
                    inputValue = valueAtIndex;
                }
            }
            else {
                inputValue = valueAtIndex || '';
            }
        }
        const inputEle = this.createElement('input', {
            id: `${this.element.id}-${index + 1}`,
            className: INPUTFIELD + ' ' + 'e-input',
            attrs: {
                maxlength: '1',
                type: this.type,
                inputmode: this.htmlAttributes['inputmode'] || (this.type === 'number' ? 'numeric' : 'text')
            }
        });
        if (this.disabled) {
            inputEle.setAttribute('disabled', 'disabled');
        }
        this.element.appendChild(inputEle);
        this.inputs.push(inputEle);
        if (inputValue) {
            inputEle.value = inputValue;
        }
        this.wireEvents(inputEle, index);
    }
    renderSeparator(index, length) {
        if (this.separator.length > 0) {
            for (let i = index; i < length; i++) {
                const separatorElement = this.createElement('span', {
                    className: 'e-otp-separator'
                });
                separatorElement.textContent = this.separator;
                this.separatorElements.push(separatorElement);
                this.element.insertBefore(separatorElement, this.inputs[parseInt(i.toString(), 10)]);
            }
        }
    }
    updateSeparatorValue() {
        if (this.separator === '') {
            this.separatorElements.forEach((element) => remove(element));
            this.separatorElements = [];
        }
        else {
            this.separatorElements.forEach((element) => {
                element.textContent = this.separator;
            });
        }
    }
    addPlaceHolder() {
        for (let i = 0; i < this.inputs.length; i++) {
            const placeholderValue = this.placeholder.length <= 1 ? this.placeholder : this.placeholder.charAt(i);
            this.setElementAttributes({ 'placeholder': placeholderValue }, this.inputs[parseInt(i.toString(), 10)]);
        }
    }
    updateInputType(inputType) {
        const inputMode = this.htmlAttributes['inputmode'] || (inputType === 'number' ? 'numeric' : 'text');
        this.inputs.forEach((input) => {
            input.type = inputType;
            input.setAttribute('inputmode', inputMode);
        });
    }
    getDefaultValue() {
        let extractedValue = typeof this.value === 'number' ? this.value.toString() : this.value;
        if (this.textTransform) {
            extractedValue = this.getTransformedText(extractedValue);
        }
        // To remove the white space if present.
        const value = extractedValue.replace(/\s/g, '');
        return value.length > 0 ? value.split('') : undefined;
    }
    getTransformedText(transformingText) {
        const transformedText = this.textTransform.toLowerCase() === TextTransform.Lowercase ? transformingText.toLowerCase() :
            this.textTransform.toLowerCase() === TextTransform.Uppercase ? transformingText.toUpperCase() : transformingText;
        return transformedText;
    }
    handleInputChange(index, event) {
        const currentInputElement = this.inputs[parseInt(index.toString(), 10)];
        if (currentInputElement && index < this.length - 1 && currentInputElement.value.length > 0) {
            const nextInputElement = this.inputs[parseInt(index.toString(), 10) + 1];
            this.shouldFireFocus = this.shouldFireBlur = false;
            nextInputElement.focus();
            if (nextInputElement && nextInputElement.value.length > 0) {
                nextInputElement.select();
            }
        }
        const target = event.target;
        if (target.value.length > 1) {
            target.value = target.value.slice(0, 1);
        }
        if (this.textTransform) {
            target.value = this.getTransformedText(target.value);
        }
        this.triggerInputEvent(index, event);
        this.triggerValuechanged(event, true);
    }
    handleKeyAction(index, event) {
        if (event.key.length > 1 && !((index === 0 && event.key === 'Backspace') ||
            (index === this.length - 1 && event.key === 'Delete'))) {
            this.shouldFireFocus = this.shouldFireBlur = false;
        }
        const currentInputElement = this.inputs[parseInt(index.toString(), 10)];
        const previousInputElement = this.inputs[parseInt(index.toString(), 10) - 1];
        const nextInputElement = this.inputs[parseInt(index.toString(), 10) + 1];
        if (event.key === 'Delete') {
            let value = '';
            if (currentInputElement.value.length > 0) {
                value = currentInputElement.value;
                currentInputElement.value = '';
            }
            else if (index !== this.inputs.length - 1) {
                value = nextInputElement.value;
                nextInputElement.value = '';
                nextInputElement.focus();
            }
            if (value.length > 0) {
                this.triggerInputEvent(index, event);
            }
        }
        else if (event.key === 'Backspace') {
            if (index !== 0 && currentInputElement.value.length === 0) {
                const previousValue = previousInputElement.value;
                previousInputElement.value = '';
                previousInputElement.focus();
                if (previousValue.length > 0) {
                    this.triggerInputEvent(index, event);
                }
            }
        }
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            if (event.key === 'ArrowLeft' && index > 0) {
                previousInputElement.focus();
                previousInputElement.select();
            }
            else if (event.key === 'ArrowRight' && index < this.inputs.length - 1) {
                nextInputElement.focus();
                nextInputElement.select();
            }
            event.preventDefault();
        }
        else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
        }
        else if (event.key !== 'Tab' && !event.shiftKey && !event.ctrlKey) {
            if (this.type === 'number' && (/\D/.test(event.key.toLocaleLowerCase()))) {
                event.preventDefault();
            }
        }
    }
    handleSelection(index) {
        const currentInputElement = this.inputs[parseInt(index.toString(), 10)];
        if (currentInputElement.value) {
            currentInputElement.select();
        }
    }
    handleFocus(index, event) {
        addClass([this.inputs[parseInt(index.toString(), 10)]], 'e-otp-input-focus');
        if (this.shouldFireFocus) {
            const eventArgs = {
                element: this.element,
                event: event,
                index: index,
                isInteracted: this.isFocusInCalled ? false : true,
                value: this.value
            };
            this.trigger('focus', eventArgs);
        }
        this.shouldFireFocus = true;
    }
    handleBlur(index, event) {
        removeClass([this.inputs[parseInt(index.toString(), 10)]], 'e-otp-input-focus');
        if (this.shouldFireBlur) {
            const eventArgs = {
                element: this.element,
                event: event,
                value: this.value,
                index: index,
                isInteracted: this.isFocusOutCalled ? false : true
            };
            this.trigger('blur', eventArgs);
        }
        this.shouldFireBlur = true;
    }
    handlePaste(index, event) {
        const clipboardData = event.clipboardData;
        if (clipboardData) {
            const pastedText = clipboardData.getData('text');
            const pastedValues = pastedText.split('');
            let pastedValueIndex = 0;
            for (let i = index; i < this.inputs.length; i++) {
                if (pastedValues.length > 0 && pastedValues[parseInt(pastedValueIndex.toString(), 10)]) {
                    this.inputs[parseInt(i.toString(), 10)].value = pastedValues[parseInt(pastedValueIndex.toString(), 10)];
                    pastedValueIndex++;
                    this.updateValueProperty();
                }
            }
            this.focusIn();
            this.triggerValuechanged(event, true);
        }
    }
    triggerInputEvent(index, event) {
        const previousValue = this.value.toString();
        this.updateValueProperty();
        const inputEventArgs = {
            element: this.element,
            event: event,
            previousValue: previousValue,
            value: this.value.toString(),
            index: index
        };
        this.trigger('input', inputEventArgs);
    }
    triggerValuechanged(event, isInteracted) {
        if (this.length === this.value.toString().length) {
            if (this.previousValue !== this.value) {
                const eventArgs = {
                    element: this.element,
                    event: event,
                    isInteracted: isInteracted ? isInteracted : false,
                    previousValue: this.previousValue,
                    value: this.value
                };
                this.trigger('valueChanged', eventArgs);
                this.previousValue = this.value.toString();
            }
        }
    }
    wireEvents(inputEle, index) {
        EventHandler.add(inputEle, 'focus', this.handleFocus.bind(this, index), this);
        EventHandler.add(inputEle, 'blur', this.handleBlur.bind(this, index), this);
        EventHandler.add(inputEle, 'input', this.handleInputChange.bind(this, index), this);
        EventHandler.add(inputEle, 'keydown', this.handleKeyAction.bind(this, index), this);
        EventHandler.add(inputEle, 'click', this.handleSelection.bind(this, index), this);
        EventHandler.add(inputEle, 'paste', this.handlePaste.bind(this, index), this);
        EventHandler.add(inputEle, 'wheel', this.handleWheelEvent, this);
    }
    unWireEvents() {
        for (let i = 0; i < this.inputs.length; i++) {
            const currentInputElement = this.inputs[parseInt(i.toString(), 10)];
            EventHandler.remove(currentInputElement, 'focus', this.handleFocus.bind(this, i));
            EventHandler.remove(currentInputElement, 'blur', this.handleBlur.bind(this, i));
            EventHandler.remove(currentInputElement, 'input', this.handleInputChange.bind(this, i));
            EventHandler.remove(currentInputElement, 'keydown', this.handleKeyAction.bind(this, i));
            EventHandler.remove(currentInputElement, 'click', this.handleSelection.bind(this, i));
            EventHandler.remove(currentInputElement, 'paste', this.handlePaste.bind(this, i));
            EventHandler.remove(currentInputElement, 'wheel', this.handleWheelEvent);
        }
    }
    updateValueProperty() {
        let value = '';
        this.inputs.forEach((input) => {
            value += input.value;
        });
        const prevOnChange = this.isProtectedOnChange;
        this.isProtectedOnChange = true;
        this.value = typeof this.value === 'number' ? parseInt(value, 10) : value;
        this.isProtectedOnChange = prevOnChange;
        this.hiddenInputEle.value = this.value.toString();
    }
    updateInputValue(previousValue) {
        let stringifiedValue = this.value.toString();
        if (this.textTransform) {
            stringifiedValue = this.getTransformedText(stringifiedValue);
        }
        const previousStringValue = previousValue.toString();
        for (let i = 0; i < this.inputs.length; i++) {
            if (previousStringValue.charAt(i) !== stringifiedValue.charAt(i)) {
                this.inputs[parseInt(i.toString(), 10)].value = stringifiedValue.charAt(i);
                this.hiddenInputEle.value = stringifiedValue;
            }
        }
        this.focusIn();
    }
    updateCssClass(addCss, removeCss = '') {
        let cssClasses;
        if (removeCss) {
            cssClasses = removeCss.trim().split(' ');
            this.element.classList.remove(...cssClasses);
        }
        if (addCss) {
            cssClasses = addCss.trim().split(' ');
            this.element.classList.add(...cssClasses);
        }
    }
    updateVariantClass() {
        const variantClass = this.stylingMode.toLocaleLowerCase() === 'outlined' ? 'outline' : this.stylingMode.toLocaleLowerCase();
        const validClasses = ['underlined', 'filled', 'outline'];
        if (validClasses.indexOf(variantClass) !== -1) {
            removeClass([this.element], validClasses.map((cls) => `e-${cls}`));
            addClass([this.element], `e-${variantClass}`);
        }
    }
    updateAriaLabel(customAriaLabel) {
        this.inputs.forEach((input, index) => {
            const defaultLabel = `Enter Otp Character ${index + 1}`;
            const ariaLabel = customAriaLabel && customAriaLabel.length > 0
                ? customAriaLabel[parseInt(index.toString(), 10)] || defaultLabel
                : defaultLabel;
            input.setAttribute('aria-label', ariaLabel);
        });
    }
    updateDisabledState() {
        this.inputs.forEach((input) => {
            if (this.disabled) {
                input.setAttribute('disabled', 'disabled');
            }
            else {
                input.removeAttribute('disabled');
            }
        });
    }
    setElementAttributes(htmlAttributes, element) {
        if (!isNullOrUndefined(htmlAttributes)) {
            for (const key in htmlAttributes) {
                if (key === 'class') {
                    const elementClass = htmlAttributes['class'].replace(/\s+/g, ' ').trim();
                    if (elementClass) {
                        addClass([element], elementClass.split(' '));
                    }
                }
                else if (key === 'inputmode') {
                    this.setInputMode(htmlAttributes[`${key}`]);
                }
                else if (key === 'name' && this.element.id === element.id) {
                    this.hiddenInputEle.setAttribute(key, htmlAttributes[`${key}`]);
                }
                else {
                    element.setAttribute(key, htmlAttributes[`${key}`]);
                }
            }
        }
    }
    setInputMode(inputModeValue) {
        for (let i = 0; i < this.inputs.length; i++) {
            this.inputs[parseInt(i.toString(), 10)].setAttribute('inputmode', inputModeValue);
        }
    }
    handleLengthChange(currentValue, previousValue) {
        const isLengthAdded = (currentValue - previousValue) > 0;
        if (isLengthAdded) {
            for (let i = previousValue; i < currentValue; i++) {
                this.createOtpInput(i);
            }
            this.renderSeparator(previousValue, currentValue);
            this.addPlaceHolder();
            this.updateAriaLabel(this.ariaLabels);
        }
        else {
            if (currentValue >= 0 && this.inputs.length > 0) {
                for (let i = currentValue; i < this.inputs.length; i++) {
                    remove(this.inputs[parseInt(i.toString(), 10)]);
                }
                this.inputs.splice(currentValue);
                if (this.separatorElements.length > 0) {
                    // separator should be completely removed when length is 0 or 1;
                    const index = currentValue === 0 ? 0 : currentValue - 1;
                    for (let i = index; i < this.separatorElements.length; i++) {
                        remove(this.separatorElements[parseInt(i.toString(), 10)]);
                    }
                    this.separatorElements.splice(index);
                }
            }
        }
    }
    /**
     * To get component name.
     *
     * @returns {string} - Module Name
     * @private
     */
    getModuleName() {
        return 'otpinput';
    }
    /**
     * To get the properties to be maintained in the persisted state.
     *
     * @returns {string} - Persist data
     */
    getPersistData() {
        return this.addOnPersist([]);
    }
    /**
     * Destroy the Otp input.
     *
     * @returns {void}
     */
    destroy() {
        super.destroy();
        this.unWireEvents();
        this.inputs.forEach((input) => { remove(input); });
        this.separatorElements.forEach((separatorElement) => { remove(separatorElement); });
        remove(this.hiddenInputEle);
        removeClass([this.element], ['e-underlined', 'e-filled', 'e-outline', 'e-rtl']);
        if (this.cssClass) {
            removeClass([this.element], this.cssClass.trim().split(' '));
        }
        this.element.removeAttribute('role');
        this.inputs = [];
        this.separatorElements = [];
        this.hiddenInputEle = null;
    }
    /**
     * Sets the focus to the Otp input for interaction.
     *
     * @returns {void}
     */
    focusIn() {
        this.isFocusInCalled = true;
        let focusIndex = this.inputs.length - 1;
        for (let index = 0; index < this.inputs.length; index++) {
            if (!(this.inputs[parseInt(index.toString(), 10)].value.length > 0)) {
                focusIndex = index;
                break;
            }
        }
        this.inputs[parseInt(focusIndex.toString(), 10)].focus();
        this.isFocusInCalled = false;
    }
    /**
     * Remove the focus from Otp input, if it is in focus state.
     *
     * @returns {void}
     */
    focusOut() {
        this.isFocusOutCalled = true;
        this.inputs.forEach((input) => {
            input.blur();
        });
        this.isFocusOutCalled = false;
    }
    /**
     * Called internally if any of the property value changed.
     *
     * @param  {OtpInputModel} newProp - Specifies new properties
     * @param  {OtpInputModel} oldProp - Specifies old properties
     * @returns {void}
     * @private
     */
    onPropertyChanged(newProp, oldProp) {
        for (const prop of Object.keys(newProp)) {
            switch (prop) {
                case 'textTransform':
                    this.updateInputValue(this.value);
                    break;
                case 'value':
                    this.updateInputValue(oldProp.value);
                    this.triggerValuechanged();
                    break;
                case 'placeholder':
                    this.addPlaceHolder();
                    break;
                case 'disabled':
                    this.updateDisabledState();
                    break;
                case 'cssClass':
                    this.updateCssClass(newProp.cssClass, oldProp.cssClass);
                    break;
                case 'separator':
                    if (oldProp.separator === '') {
                        this.renderSeparator(1, this.inputs.length);
                    }
                    else {
                        this.updateSeparatorValue();
                    }
                    break;
                case 'htmlAttributes':
                    this.setElementAttributes(newProp.htmlAttributes, this.element);
                    break;
                case 'type':
                    this.updateInputType(newProp.type);
                    break;
                case 'stylingMode':
                    this.updateVariantClass();
                    break;
                case 'ariaLabels':
                    this.updateAriaLabel(newProp.ariaLabels);
                    break;
                case 'length':
                    this.handleLengthChange(newProp.length, oldProp.length);
                    break;
                case 'enableRtl':
                    this.element.classList[this.enableRtl ? 'add' : 'remove'](RTL);
                    break;
                case 'autoFocus':
                    if (this.autoFocus) {
                        this.focusIn();
                    }
                    break;
            }
        }
    }
};
__decorate([
    Property(4)
], OtpInput.prototype, "length", void 0);
__decorate([
    Property('')
], OtpInput.prototype, "value", void 0);
__decorate([
    Property(OtpInputType.Number)
], OtpInput.prototype, "type", void 0);
__decorate([
    Property('')
], OtpInput.prototype, "separator", void 0);
__decorate([
    Property('')
], OtpInput.prototype, "placeholder", void 0);
__decorate([
    Property(OtpInputStyle.Outlined)
], OtpInput.prototype, "stylingMode", void 0);
__decorate([
    Property(false)
], OtpInput.prototype, "disabled", void 0);
__decorate([
    Property('')
], OtpInput.prototype, "cssClass", void 0);
__decorate([
    Property(false)
], OtpInput.prototype, "autoFocus", void 0);
__decorate([
    Property(TextTransform.None)
], OtpInput.prototype, "textTransform", void 0);
__decorate([
    Property({})
], OtpInput.prototype, "htmlAttributes", void 0);
__decorate([
    Property([])
], OtpInput.prototype, "ariaLabels", void 0);
__decorate([
    Event()
], OtpInput.prototype, "created", void 0);
__decorate([
    Event()
], OtpInput.prototype, "valueChanged", void 0);
__decorate([
    Event()
], OtpInput.prototype, "focus", void 0);
__decorate([
    Event()
], OtpInput.prototype, "blur", void 0);
__decorate([
    Event()
], OtpInput.prototype, "input", void 0);
OtpInput = __decorate([
    NotifyPropertyChanges
], OtpInput);
export { OtpInput };
